import { useEffect, useRef, useState } from "react";

import {
  date2FormattedString,
  month2String,
  string2Datetime,
  time2FormattedString,
  useDidMountEffect,
} from "../../utils/utils";

export default function useChargingHistory(
  locationClient,
  wallboxId = null,
  showMsg = null,
  isFocused = true,
) {
  const [chargingHistory, setChargingHistory] = useState(null);
  const [chargingHistoryStats, setChargingHistoryStats] = useState(null);
  const [batch, setBatch] = useState(0);
  const [nPages, setNPages] = useState(null);
  const [exportDate, setExportDate] = useState(new Date());

  const batchSize = 20;
  const lastBatch = useRef(null);

  const historyRequest = async (_batch) => {
    const params = {
      order_by: "-id",
      batch: _batch,
      batch_size: batchSize,
      wallbox: wallboxId,
    };
    const response = await locationClient.getChargingHistory(params);
    return response;
  };

  const addHistory = async () => {
    if (lastBatch.current === null) return 
    if (nPages !== null && lastBatch.current + 1 >= nPages) return true;
    try {
      lastBatch.current = lastBatch.current + 1;
      const response = await historyRequest(lastBatch.current || 0);
      setChargingHistory((r) => [...r, ...response.data]);
      return true;
    } catch (error) {
      console.warn('Error in "addHistory"', error, error.stack);
      return false;
    }
  };

  const getHistory = async () => {
    try {
      lastBatch.current = batch;
      const response = await historyRequest(batch);
      setChargingHistory(response.data);
      setNPages(Number(response.headers.length));
      return true;
    } catch (error) {
      console.warn('Error in "getHistory"', error, error.stack);
      return false;
    }
  };

  const getHistoryStats = async () => {
    try {
      const response = await locationClient.getChargingHistoryStats({
        wallbox: wallboxId,
      });
      setChargingHistoryStats(response.data);
      return true;
    } catch (error) {
      console.warn('Error in "getHistoryStats"', error, error.stack);
      return false;
    }
  };

  const loadData = async () => {
    await Promise.all([getHistory(), getHistoryStats()]);
  };

  const clearData = () => {
    setChargingHistory(null);
    setChargingHistoryStats(null);
  };

  const initData = () => {
    if (batch === 0) loadData();
    else {
      setBatch(0);
      getHistoryStats();
    }
  };

  const extendData = addHistory;

  useEffect(() => {
    if (isFocused) initData();
    else clearData();
  }, [isFocused]);

  useDidMountEffect(() => {
    loadData();
  }, [batch]);

  const fetchExcelData = async () => {
    try {
      const response = await locationClient.getChargingHistory({
        month: month2String(exportDate),
      });
      const history = response.data;
      return history;
    } catch (error) {
      console.warn('Error in "getExcelData"', error, error.stack);
      showMsg?.("Ladehistorie konnte nicht geladen werden!", "error");
      return null;
    }
  };

  const getExcelData = async () => {
    const history = await fetchExcelData();
    if (history === null) return;
    if (history.length > 0) {
      return Object.values(
        Object.groupBy(history, ({ wallbox }) => wallbox),
      ).map((wbHistory) => {
        const wallboxName = wbHistory[0].wallbox_name;
        return createExcelSheet(wallboxName, wbHistory);
      });
    } else {
      return [createExcelSheet("Ladehistorie", history)];
    }
  };

  const createExcelSheet = (sheetName, chargingHistoryToDownload) => {
    const excelRows = [
      ["Datum", "Lademenge [kWh]", "Ladedauer [Std]", "Ladestart", "Ladeende"],
    ];
    chargingHistoryToDownload.forEach((info) => {
      excelRows.push([
        date2FormattedString(string2Datetime(info.start_time)),
        info.energy ? info.energy.toFixed(2) : "0",
        info.duration,
        time2FormattedString(string2Datetime(info.start_time)),
        time2FormattedString(string2Datetime(info.end_time)),
      ]);
    });
    return { name: sheetName, data: excelRows };
  };

  return {
    chargingHistory,
    chargingHistoryStats,
    data: {
      init: initData,
      update: loadData,
      clear: clearData,
      extend: extendData,
    },
    excelExport: {
      getData: getExcelData,
      filename: `Ladehistorie-${exportDate ? month2String(exportDate) : ""}`,
      disabled: !exportDate,
      datePicker: { date: exportDate, setDate: setExportDate },
    },
    pagination: { page: batch, setPage: setBatch, nPages },
  };
}
